import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import axios from "axios";
import {
  ArrowRight,
  Send,
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ChevronUp,
} from "lucide-react";
import {
  projects as mockProjects,
  blogPosts as mockBlogPosts,
  trustedBy,
  companyInfo,
  footerData,
} from "../data/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ===== PROJECTS SECTION ===== */
export const ProjectsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [projects, setProjects] = useState(mockProjects.slice(0, 4));

  useEffect(() => {
    axios.get(`${API}/projects`).then(res => {
      if (res.data && res.data.length > 0) setProjects(res.data.slice(0, 4));
    }).catch(() => {});
  }, []);
  return (
    <section id="projects" className="projects-section" ref={ref}>
      <div className="projects-inner">
        <motion.div
          className="projects-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="projects-title">Our Latest Projects</h2>
          <div className="section-line" />
          <Link to="/projects" className="btn-outline-dark" style={{ marginTop: '24px' }}>ALL PROJECTS</Link>
        </motion.div>
        <div className="projects-grid">
          {projects.map((project, i) => (
            <motion.div
              key={project.id}
              className="project-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
            >
              <div className="project-image">
                <img
                  src={project.image}
                  alt={project.title}
                  loading="lazy"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== TRUSTED BY SECTION ===== */
export const TrustedBySection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  
  const logoFiles = [
    '1.png', '2.jpg', '3.png', '4.png', '5.png', '6.png', '7.jpg', '8.png', '9.jpg', '10.png',
    '11.png', '12.png', '13.png', 'Picture14.jpg', 'Picture15.png', 'Picture16.png', 'Picture17.png',
    'Picture18.png', 'Picture19.png', 'Picture20.png', 'Picture21.jpg', 'Picture22.png', 'Picture23.jpg',
    'Picture24.png', 'Picture25.png', 'Picture26.png', 'Picture27.png', 'Picture28.jpg', 'Picture29.jpg',
    'Picture30.png', 'Picture31.png', 'Picture32.png', 'Picture33.jpg', 'Picture34.jpg', 'Picture35.png',
    'Picture36.png', 'Picture37.jpg', 'Picture38.png', 'Picture39.png', 'Picture40.jpg'
  ];
  
  return (
    <section className="trusted-section" ref={ref}>
      <motion.div
        className="trusted-inner"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h3 className="trusted-title">Trusted By Industry Leaders</h3>
        <div className="trusted-scroll">
          <div className="trusted-track">
            {[...logoFiles, ...logoFiles].map((logo, i) => (
              <div key={i} className="trusted-logo">
                <img src={`/logos/${logo}`} alt={`Company ${i + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

/* ===== BLOG SECTION ===== */
export const BlogSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [blogPosts, setBlogPosts] = useState(mockBlogPosts);

  useEffect(() => {
    axios.get(`${API}/blog`).then(res => {
      if (res.data && res.data.length > 0) setBlogPosts(res.data);
    }).catch(() => {});
  }, []);
  return (
    <section id="blog" className="blog-section" ref={ref}>
      <div className="blog-inner">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="blog-main-title">Thoughts & Experiments</h2>
          <div className="section-line" />
        </motion.div>
        <div className="blog-grid">
          {blogPosts.map((post, i) => (
            <motion.div
              key={post.id}
              className="blog-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.15 }}
            >
              <div className="blog-image">
                <img src={post.image} alt={post.title} loading="lazy" />
                <div className="blog-date">
                  <span className="blog-day">{post.date}</span>
                  <span className="blog-month">{post.month}</span>
                  <span className="blog-year">{post.year}</span>
                </div>
              </div>
              <div className="blog-meta">
                By <span className="blog-author">{post.author}</span> — In{" "}
                <span className="blog-category">{post.category}</span>
              </div>
              <h3 className="blog-title">{post.title}</h3>
              <button className="btn-dark-sm">Read More</button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== CTA SECTION ===== */
export const CTASection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <section className="cta-section" ref={ref}>
      <motion.div
        className="cta-inner"
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h2 className="cta-title">
          TRANSFORM YOUR IDEA INTO WORKING PART - START NOW
        </h2>
        <p className="cta-desc">
          Upload your 3D model and get an instant quote. From prototyping to batch production — we make 3D printing accessible for everyone in Bangalore and beyond.
        </p>
        <a 
          href={companyInfo.whatsapp || "https://wa.me/916360283627?text=Hi%2C%20I%20would%20like%20to%20get%20a%20quote%20for%203D%20printing"} 
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
        >
          CONNECT WITH OUR EXPERT <ArrowRight size={18} />
        </a>
      </motion.div>
    </section>
  );
};

/* ===== CONTACT SECTION ===== */
export const ContactSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");
    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section" ref={ref}>
      <div className="contact-inner">
        <motion.div
          className="contact-info"
          initial={{ opacity: 0, x: -30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="contact-title">Get In Touch</h2>
          <div className="section-line light-line" />
          <p className="contact-desc">
            Ready to bring your designs to life? Contact us for a free consultation and quote on your 3D printing project.
          </p>
          <div className="contact-details">
            <div className="contact-detail-item">
              <MapPin size={20} />
              <div>
                <strong>Our Office</strong>
                <p>{companyInfo.address}</p>
              </div>
            </div>
            <div className="contact-detail-item">
              <Phone size={20} />
              <div>
                <strong>Call Us</strong>
                <p>{companyInfo.phone}</p>
              </div>
            </div>
            <div className="contact-detail-item">
              <Mail size={20} />
              <div>
                <strong>Email Us</strong>
                <p>{companyInfo.email}</p>
              </div>
            </div>
            <div className="contact-detail-item">
              <Clock size={20} />
              <div>
                <strong>Working Hours</strong>
                <p>{companyInfo.workingHours}</p>
              </div>
            </div>
          </div>
        </motion.div>
        <motion.div
          className="contact-form-wrap"
          initial={{ opacity: 0, x: 30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3>Request a Quote</h3>
          {submitted && (
            <div className="form-success">Thank you! We'll get back to you within 24 hours.</div>
          )}
          {errorMsg && (
            <div className="form-success" style={{ borderColor: '#e74c3c', background: 'rgba(231,76,60,0.15)' }}>{errorMsg}</div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                placeholder="Your Name *"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <input
                type="email"
                placeholder="Your Email *"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="form-row">
              <input
                type="tel"
                placeholder="Phone Number *"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <select
                value={formData.service}
                onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                required
              >
                <option value="">Select Service *</option>
                <option value="fdm">FDM 3D Printing</option>
                <option value="sla">SLA 3D Printing</option>
                <option value="sls">SLS 3D Printing</option>
                <option value="dmls">DMLS Metal Printing</option>
                <option value="product">Product Development</option>
                <option value="other">Other</option>
              </select>
            </div>
            <textarea
              placeholder="Tell us about your project..."
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
            <button type="submit" className="btn-primary" disabled={submitting}>
              <Send size={16} />
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

/* ===== FOOTER ===== */
export const Footer = () => {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const quickLinks = [
    { label: "About Us", to: "/about" },
    { label: "Our Services", to: "/services" },
    { label: "Projects", to: "/projects" },
    { label: "Blog", to: "/blog" },
    { label: "Get a Quote", to: "/contact" },
    { label: "Careers", to: "/careers" },
    { label: "Terms and Conditions", to: "#" },
  ];

  return (
    <footer className="site-footer">
      <div className="footer-inner footer-4col">
        <div className="footer-col footer-about">
          <div className="footer-logo">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="36" height="36">
              <path d="M20 4L36 12V28L20 36L4 28V12L20 4Z" stroke="#c54b2a" strokeWidth="2" fill="none" />
              <path d="M20 4L36 12L20 20L4 12L20 4Z" fill="#c54b2a" opacity="0.3" />
              <path d="M20 20V36" stroke="#c54b2a" strokeWidth="2" />
            </svg>
            <span>3D Printzkart</span>
          </div>
          <p>{footerData.about}</p>
          <div className="footer-social">
            <a href="#" aria-label="Facebook"><Facebook size={18} /></a>
            <a href="#" aria-label="Twitter"><Twitter size={18} /></a>
            <a href="#" aria-label="Instagram"><Instagram size={18} /></a>
            <a href="#" aria-label="LinkedIn"><Linkedin size={18} /></a>
          </div>
        </div>
        <div className="footer-col">
          <h4>Quick Links</h4>
          <ul>
            {quickLinks.map((link, i) => (
              <li key={i}><Link to={link.to}>{link.label}</Link></li>
            ))}
          </ul>
        </div>
        <div className="footer-col">
          <h4>Book a Meeting</h4>
          <ul>
            <li><a href={companyInfo.calendly || "https://calendly.com/3dprintzkart"} target="_blank" rel="noopener noreferrer">📅 Schedule on Calendly</a></li>
            <li><a href={companyInfo.googleMeet || "https://meet.google.com/3dprintzkart"} target="_blank" rel="noopener noreferrer">📹 Join Google Meet</a></li>
            <li><a href={companyInfo.whatsapp} target="_blank" rel="noopener noreferrer">💬 WhatsApp Chat</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Contact Info</h4>
          <div className="footer-contact">
            <p><MapPin size={16} /> {companyInfo.address}</p>
            <p><Phone size={16} /> {companyInfo.phone}</p>
            <p><Mail size={16} /> {companyInfo.email}</p>
            <p><Clock size={16} /> {companyInfo.workingHours}</p>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} 3D Printzkart. All Rights Reserved. | Best 3D Printing Service in Bangalore, India</p>
        <button className="back-to-top" onClick={scrollTop} aria-label="Back to top">
          <ChevronUp size={20} />
        </button>
      </div>
    </footer>
  );
};
